import type {
  AccessibilityCategory,
  AccessibilityTag,
} from "../types/accessibility";

type Space = AccessibilityCategory["tags"][number];
type SpaceCategory = Pick<
  AccessibilityCategory,
  "catId" | "catImage" | "catImageDark"
>;

/**
 * The space's display name: the area heading, else the leading `qtagsAll` entry.
 * Never `qtags[0]` — since the tagging migration that row is a detail answer.
 *
 * @returns "" when neither is present
 */
export function resolveSpaceName(
  space: Pick<Space, "areaName" | "qtagsAll">,
): string {
  return space.areaName || space.qtagsAll?.split(",")[0]?.trim() || "";
}

/**
 * The tag a flag on a space is filed against — what the flag form shows as the
 * space and what `review/accessibility-report-issue` receives as `category_id`,
 * `type_question_id` and `type_question_tag_id`.
 *
 * The backend sends a space in one of two shapes:
 *
 * - **Legacy.** The space's type tag ("Main entrance") is a row in `qtags`, so
 *   it is found by name and sent as it is.
 * - **After the tagging migration (DEV-551).** The type tag is gone from
 *   `qtags`, which holds only the detail tags, and the space is named by
 *   `areaName` / `areaTagId` / `areaHeadingId` instead. Looking it up by name
 *   finds nothing, which is what left the flag button dead (BUG-448).
 *
 * For a migrated space the three ids are built to what the backend resolves
 * the area from (`ReportIssueFlag`: the child heading of `category_id` whose
 * `migrated_from_tag_id` is `type_question_tag_id`):
 *
 * - `categoryId` is the **category's** id. A detail tag's own `categoryId` is
 *   the area heading on this shape, not the category, so it must not be taken
 *   from `qtags`.
 * - `tagId` is `areaTagId`.
 * - `questionId` is 0: the type question no longer exists, and 0 is what the
 *   backend expects in its place.
 *
 * Returns undefined when the space can be identified neither way. It never
 * falls back to an arbitrary detail tag: that files the flag against the wrong
 * thing.
 */
export function resolveSpaceFlagTag(
  space: Space,
  category: SpaceCategory,
): AccessibilityTag | undefined {
  const spaceName = space.qtagsAll?.split(",")[0]?.trim() ?? "";

  if (space.areaTagId != null) {
    const image = space.areaImage ?? category.catImage;
    return {
      categoryId: category.catId,
      categoryDisplaySectionId: 0,
      categoryDisplaySectionName: "",
      questionId: 0,
      tagId: space.areaTagId,
      tagName: space.areaName || spaceName,
      tagCount: 0,
      tagNote: null,
      tagOwnerNote: null,
      tagHash: "",
      reviewByUsername: "",
      reviewByUserid: 0,
      tag_image: image,
      tag_image_dark: space.areaImageDark ?? category.catImageDark ?? image,
      tag_image_selected: "",
      tag_image_selected_dark: "",
      user_roles: { isGeneralUser: false },
    };
  }

  if (!spaceName) return undefined;

  return space.qtags?.find((tag) => tag.tagName === spaceName);
}
